import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, Clock, ShieldCheck, FileText } from "lucide-react";
import Navigation from "@/components/site/Navigation";
import Footer from "@/components/site/Footer";
import QuoteForm from "@/components/site/QuoteForm";
import { getServiceBySlug, SLUG_TO_SITE_TYPE } from "@/data/services";

const PROMISES = [
  { icon: Clock, k: "24h", v: "Quotation turnaround" },
  { icon: FileText, k: "Itemised", v: "Transparent line-item pricing" },
  { icon: ShieldCheck, k: "Free", v: "Site survey · no obligation" },
];

export default function QuotePage() {
  const { slug } = useParams();
  const service = slug ? getServiceBySlug(slug) : null;
  const initialSiteType = slug ? SLUG_TO_SITE_TYPE[slug] : null;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  return (
    <div data-testid={`quote-page${slug ? `-${slug}` : ""}`} className="bg-white text-[#050B14]">
      <Navigation />

      <main>
        <section className="relative bg-[#F4F8FC] pt-40 md:pt-48 pb-12 md:pb-16 border-b border-[#E5EBF2]">
          <div className="relative container-ags">
            <Link
              to={service ? `/services/${service.slug}` : "/"}
              data-testid="quote-back-link"
              className="group inline-flex items-center gap-2 text-sm font-medium text-[#0E2A57] hover:text-[#2BB6E5] transition-colors mb-8"
            >
              <ArrowLeft size={16} className="transition-transform group-hover:-translate-x-1" />
              {service ? `Back to ${service.title}` : "Back to home"}
            </Link>

            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-16 bg-[#2BB6E5]" />
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-[#0E2A57] font-semibold">
                {service ? service.tag : "11 — Request a Quote"}
              </span>
            </div>
            <h1 className="font-heading font-extrabold text-5xl md:text-7xl leading-[1.0] tracking-[-0.04em] text-[#050B14] max-w-4xl">
              Request a quote
              {service ? (
                <>
                  {" "}for{" "}
                  <span className="italic-display font-normal text-[#0E2A57]">
                    {service.title.replace(" Cleaning", "")}
                  </span>
                  .
                </>
              ) : (
                "."
              )}
            </h1>
            <p className="mt-6 text-[#475569] text-lg md:text-xl leading-relaxed max-w-2xl">
              Tell us about your premises and we'll come back with an itemised
              proposal within 24 hours. No obligation, no chasing.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white border border-[#E5EBF2]">
              {PROMISES.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.k} className="p-5 md:p-6 flex items-center gap-4">
                    <span className="bg-[#0E2A57] text-white w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <Icon size={16} />
                    </span>
                    <div>
                      <div className="font-heading font-extrabold text-xl text-[#0E2A57] tracking-[-0.035em]">
                        {p.k}
                      </div>
                      <div className="text-xs text-[#737373] mt-0.5">{p.v}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {service && (
          <section className="bg-white py-10 md:py-12 border-b border-[#E5EBF2]">
            <div className="container-ags flex flex-wrap gap-3">
              {service.features.slice(0, 4).map((f) => (
                <span
                  key={f}
                  className="inline-flex items-center gap-2 text-sm text-[#0E2A57] bg-[#F4F8FC] px-3 py-2 border border-[#E5EBF2]"
                >
                  <Check size={14} strokeWidth={3} className="text-[#2BB6E5]" />
                  {f}
                </span>
              ))}
            </div>
          </section>
        )}

        <QuoteForm initialSiteType={initialSiteType} hideFrequency={true} />
      </main>

      <Footer />
    </div>
  );
}
